import { motion } from 'framer-motion';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import SectionTitle from '../../components/ui/SectionTitle';

const AboutLocation = () => {
  const { t } = useLanguage();

  return (
    <section className="py-20 bg-neutral-50">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Map Column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative h-full"
          >
            <div className="h-full relative rounded-lg shadow-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1739.959338569762!2d-21.928471483883666!3d64.14469761638455!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48d674c7c735704b%3A0xcddb30a944ea193b!2sRossopomodoro!5e0!3m2!1sen!2sus!4v1749820966602!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="Restaurant Location"
                className="absolute inset-0"
              />
              <div className="absolute bottom-4 left-4 bg-white px-4 py-2 rounded-lg shadow-md flex items-center gap-2">
                <div className="w-3 h-3 bg-red-800 rounded-full"></div>
                <span className="text-sm font-medium text-neutral-700">Rossopomodoro</span>
              </div>
            </div>
          </motion.div>

          {/* Text Column */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-6 flex flex-col h-full"
          >
            <SectionTitle title={t.about.location.title} align="left" className="mb-8">
              <p className="text-neutral-700 mt-4">{t.about.location.subtitle}</p>
            </SectionTitle>

            <div className="space-y-4 text-neutral-700 flex-grow">
              {t.about.location.content.map((paragraph, index) => (
                <p key={index} className="leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-800 mt-auto">
              {/* Points of Interest Cards */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="grid grid-cols-2 gap-4 mt-auto"
              >
                {/* Hallgrímskirkja Card */}
                <div className="bg-white p-4 rounded-lg shadow-md border border-neutral-200 hover:shadow-lg transition-shadow">
                  <div className="aspect-square mb-3 overflow-hidden rounded-md">
                    <img
                      src="https://oupvappnlmhqopqsewde.supabase.co/storage/v1/object/public/properties/hallgrimskirkja.png"
                      alt="Hallgrímskirkja church - iconic landmark near Rossopomodoro Reykjavik"
                      title="Hallgrímskirkja - 5 minute walk from Rossopomodoro"
                      loading="lazy"
                      width="200"
                      height="200"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h4 className="font-serif text-lg font-semibold text-neutral-900 mb-1">
                    {t.about.location.poi.hallgrimskirkja.name}
                  </h4>
                  <div className="text-sm text-neutral-600">
                    <p className="font-medium">{t.about.location.poi.hallgrimskirkja.distance}</p>
                    <p>{t.about.location.poi.hallgrimskirkja.walkTime}</p>
                  </div>
                </div>

                {/* Harpa Card */}
                <div className="bg-white p-4 rounded-lg shadow-md border border-neutral-200 hover:shadow-lg transition-shadow">
                  <div className="aspect-square mb-3 overflow-hidden rounded-md">
                    <img
                      src="https://oupvappnlmhqopqsewde.supabase.co/storage/v1/object/public/properties/harpa.png"
                      alt="Harpa Concert Hall - cultural landmark near Rossopomodoro Reykjavik"
                      title="Harpa Concert Hall - 13 minute walk from Rossopomodoro"
                      loading="lazy"
                      width="200"
                      height="200"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h4 className="font-serif text-lg font-semibold text-neutral-900 mb-1">
                    {t.about.location.poi.harpa.name}
                  </h4>
                  <div className="text-sm text-neutral-600">
                    <p className="font-medium">{t.about.location.poi.harpa.distance}</p>
                    <p>{t.about.location.poi.harpa.walkTime}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutLocation;
